import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Bitcoin, Mail, Lock, User, ArrowRight, Loader2, Gift } from 'lucide-react';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register, isAuthenticated } = useAuth();

  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const referralFromUrl = searchParams.get('ref') || '';

  // Form states
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [registerUsername, setRegisterUsername] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerReferral, setRegisterReferral] = useState(referralFromUrl);

  useEffect(() => {
    if (referralFromUrl) {
      setActiveTab('register');
      setRegisterReferral(referralFromUrl);
    }
  }, [referralFromUrl]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(loginEmail, loginPassword);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      const message = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (registerPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      await register(registerUsername, registerEmail, registerPassword, registerReferral || null);
      toast.success(registerReferral ? 'Account created! Your referrer got a bonus 🎉' : 'Account created! Welcome to SatoshiShort!');
      navigate('/dashboard');
    } catch (err) {
      const message = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#F7931A]/10 border border-[#F7931A]/30 mb-4">
            <Bitcoin className="w-8 h-8 text-[#F7931A]" />
          </div>
          <h1 className="font-heading text-2xl font-bold">
            {activeTab === 'login' ? 'Welcome Back' : 'Join SatoshiShort'}
          </h1>
          <p className="text-slate-400 mt-2">
            {activeTab === 'login'
              ? 'Sign in to continue earning Bitcoin'
              : 'Create your account and start earning'}
          </p>
        </div>

        {/* Auth Card */}
        <div className="card-dark p-6 sm:p-8">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#0D0F17] mb-6">
              <TabsTrigger
                value="login"
                className="data-[state=active]:bg-[#F7931A] data-[state=active]:text-black"
              >
                Login
              </TabsTrigger>
              <TabsTrigger
                value="register"
                className="data-[state=active]:bg-[#F7931A] data-[state=active]:text-black"
              >
                Register
              </TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-slate-300">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="pl-10 bg-[#0D0F17] border-white/10 focus:border-[#F7931A]/50"
                      required
                      data-testid="auth-login-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-slate-300">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      className="pl-10 bg-[#0D0F17] border-white/10 focus:border-[#F7931A]/50"
                      required
                      data-testid="auth-login-password-input"
                    />
                  </div>
                </div>

                {error && activeTab === 'login' && (
                  <p className="text-sm text-red-400 bg-red-400/10 px-3 py-2 rounded-lg">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full btn-gold py-5"
                  disabled={loading}
                  data-testid="auth-login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Sign In
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="register-username" className="text-slate-300">
                    Username
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="register-username"
                      type="text"
                      placeholder="satoshi_nakamoto"
                      value={registerUsername}
                      onChange={(e) => setRegisterUsername(e.target.value)}
                      className="pl-10 bg-[#0D0F17] border-white/10 focus:border-[#F7931A]/50"
                      required
                      data-testid="auth-register-username-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-email" className="text-slate-300">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@example.com"
                      value={registerEmail}
                      onChange={(e) => setRegisterEmail(e.target.value)}
                      className="pl-10 bg-[#0D0F17] border-white/10 focus:border-[#F7931A]/50"
                      required
                      data-testid="auth-register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-password" className="text-slate-300">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerPassword}
                      onChange={(e) => setRegisterPassword(e.target.value)}
                      className="pl-10 bg-[#0D0F17] border-white/10 focus:border-[#F7931A]/50"
                      required
                      data-testid="auth-register-password-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500">Minimum 6 characters</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-referral" className="text-slate-300 flex items-center gap-2">
                    Referral Code
                    <span className="text-xs text-slate-500 font-normal">(optional)</span>
                  </Label>
                  <div className="relative">
                    <Gift className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                    <Input
                      id="register-referral"
                      type="text"
                      placeholder="Enter referral code"
                      value={registerReferral}
                      onChange={(e) => setRegisterReferral(e.target.value.toUpperCase())}
                      className="pl-10 bg-[#0D0F17] border-white/10 focus:border-[#F7931A]/50 uppercase"
                      data-testid="auth-register-referral-input"
                    />
                  </div>
                  {registerReferral && (
                    <p className="text-xs text-emerald-400">
                      Your referrer will earn a 100 sat bonus when you sign up!
                    </p>
                  )}
                </div>

                {error && activeTab === 'register' && (
                  <p className="text-sm text-red-400 bg-red-400/10 px-3 py-2 rounded-lg">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full btn-gold py-5"
                  disabled={loading}
                  data-testid="auth-register-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>

        {/* Additional Info */}
        <p className="text-center text-sm text-slate-500 mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default AuthPage;
